import api from "../../../utils/api";
import {
  STATEMENT_ENDPOINTS,
  type CoverageRow,
  type Expense,
  type ExpenseAccount,
  type MovementKind,
  type PeriodClash,
  type Reconciliation,
  type StatementFormat,
  type StatementPeriod,
  type StatementRow,
  type StatementTemplate,
} from "@torre/expenses";
import type { ImportRecord, PreviewCharge } from "./expensesService";

/** Cargo propuesto con su tipo de movimiento y devolución (#954). */
export type StatementPreviewCharge = PreviewCharge & {
  kind?: MovementKind;
  kindReason?: string | null;
  relatedAccountId?: string | null;
  revertsExpenseId?: string | null;
  revertsIndex?: number | null;
};

/** Importación con cuenta, formato y conciliación (#952 – #954). */
export type StatementImportRecord = Omit<ImportRecord, "charges"> & {
  charges: StatementPreviewCharge[];
  format?: "ia" | StatementFormat;
  accountId?: string | null;
  accountSuggested?: boolean;
  periodClash?: PeriodClash | null;
  reconciliation?: Reconciliation;
  reconcileStatus?: Reconciliation["status"];
};

// ---------------------------------------------------------------------------
// Cliente API de estados de cuenta del banco (#952 – #954): cuentas,
// cobertura, importación CSV/Excel/OFX y conciliación. Mismo formato de
// respuesta `{ meta, data }` que el resto de Gastos.
// ---------------------------------------------------------------------------

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };
const data = <T>(res: any): T => res?.data?.data as T;
const E = STATEMENT_ENDPOINTS;

export const fetchAccounts = async () => data<ExpenseAccount[]>(await api.get(E.accountList));
export const saveAccount = async (payload: Partial<ExpenseAccount>, id?: string) =>
  data<ExpenseAccount>(id ? await api.put(E.account, payload, { params: { id } }) : await api.post(E.account, payload));
export const deleteAccount = async (id: string) => data(await api.delete(E.account, { params: { id } }));

export const fetchCoverage = async (params: { from?: string; to?: string }) =>
  data<{ from: string; to: string; rows: CoverageRow[]; unassigned: number }>(await api.get(E.coverage, { params }));

export interface StatementImportPayload {
  accountId: string;
  format: StatementFormat;
  rows: StatementRow[];
  statementPeriod?: StatementPeriod | null;
  statementTotal?: number | null;
  template?: StatementTemplate | null;
  saveTemplate?: boolean;
}

export async function importStatement(file: File, payload: StatementImportPayload) {
  const form = new FormData();
  form.append("file", file);
  form.append("accountId", payload.accountId);
  form.append("format", payload.format);
  form.append("rows", JSON.stringify(payload.rows));
  if (payload.statementPeriod) form.append("statementPeriod", JSON.stringify(payload.statementPeriod));
  if (payload.statementTotal != null) form.append("statementTotal", String(payload.statementTotal));
  if (payload.template) form.append("template", JSON.stringify(payload.template));
  form.append("saveTemplate", String(payload.saveTemplate !== false));
  return data<StatementImportRecord>(await api.post(E.statementImport, form, { ...MULTIPART, timeout: 120_000 }));
}

export const updateImportMeta = async (
  id: string,
  payload: { accountId?: string | null; statementPeriod?: StatementPeriod | null; statementTotal?: number | null },
) => data<StatementImportRecord & { overlaps?: string[] }>(await api.put(E.importMeta, payload, { params: { id } }));

export const linkRefund = async (id: string, revertsExpenseId: string | null) =>
  data<Expense>(await api.put(E.refundLink, { revertsExpenseId }, { params: { id } }));
